import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

import { getClaimFact } from "./helpers"

const header = [
	["Subject: Claims Bordereaux"],
	["Please find enclosed Claims Bordereaux"],
	[]
]

/****************** Helper Functions ******************/
const getSettlementMonth = claim => {
	return dayjs(claim?.transactions?.[0]?.dateTime).format("MMM-YY") //TODO: find transaction based on dateTime
}
const getLossDate = claim => {
	const date = getClaimFact("lossDate", "dateValue", claim)
	return dayjs(date).format("DD-MMM-YY")
}
const getAdjusterFeeDate = claim => {
	const date = getClaimFact("adjusterFeeDate", "dateValue", claim)
	return dayjs(date).format("DD-MMM-YY")
}
const getLegalFeeDate = claim => {
	const date = getClaimFact("legalFeeDate", "dateValue", claim)
	return dayjs(date).format("DD-MMM-YY")
}

/****************** Define Rows ******************/
const getSettledClaims = claims => {
	let output = [
		["Claim No.", "Client", "Currency", "Settled Amount", "Month of Settle"]
	]
	claims
		?.reverse()
		?.filter(claim => claim?.status === "APPROVED") //TODO: FILTER by claims approved this month -> filterPayload
		?.map(claim => {
			let policy = claim?.policy
			output.push([
				claim?.issuerNumber,
				policy?.contractHolder?.name,
				claim?.transactions?.[0]?.currencyCode, //TODO: find transaction based on dateTime
				claim?.transactions?.[0]?.amount,
				getSettlementMonth(claim)
			])
		})
	return output
}

const getAdjusterLegalFees = claims => {
	let output = [["Claim No.", "Client", "Currency", "Amount", "Date"]]
	claims
		?.reverse() // ?.filter(claim => claim?.status === "APPROVED")
		?.map(claim => {
			let policy = claim?.policy
			// Adjuster Fee
			getClaimFact("adjusterFee", "numberValue", claim) &&
				output.push([
					claim?.issuerNumber,
					policy?.contractHolder?.name,
					getClaimFact("adjusterFeeCurrency", "stringValue", claim),
					getClaimFact("adjusterFee", "numberValue", claim),
					getAdjusterFeeDate(claim)
				])
			// Legal Fee
			getClaimFact("legalFee", "numberValue", claim) &&
				output.push([
					claim?.issuerNumber,
					policy?.contractHolder?.name,
					getClaimFact("legalFeeCurrency", "stringValue", claim),
					getClaimFact("legalFee", "numberValue", claim),
					getLegalFeeDate(claim)
				])
		})
	return output
}

const getOutstandingClaims = claims => {
	let output = [
		["Claim No.", "Client", "Currency", "Reserve Amount", "Date of Loss"]
	]
	claims
		?.reverse()
		?.filter(claim => claim?.status === "PROCESSING")
		?.map(claim => {
			let policy = claim?.policy
			output.push([
				claim?.issuerNumber,
				policy?.contractHolder?.name,
				claim?.totalValues?.approvedCurrency,
				claim?.totalValues?.approvedAmount,
				getLossDate(claim)
			])
		})
	return output
}

const getSettledClaimsTotalSum = claims => {
	let output = []
	let groupedTotalSettleAmount = {}
	claims
		?.reverse()
		?.filter(claim => claim?.status === "APPROVED")
		?.map(claim => {
			let currencyCode = claim?.transactions?.[0]?.currencyCode //TODO: find transaction based on dateTime
			let settledAmount = claim?.transactions?.[0]?.amount

			if (!currencyCode) return
			if (!Object.keys(groupedTotalSettleAmount).includes(currencyCode)) {
				groupedTotalSettleAmount[currencyCode] = []
			}

			groupedTotalSettleAmount[currencyCode].push(settledAmount)
		})
	Object.keys(groupedTotalSettleAmount)?.map(objKey => {
		output.push([
			"",
			"Total",
			objKey,
			groupedTotalSettleAmount?.[objKey]?.reduce((sum, item) => {
				return (sum += parseFloat(item))
			}, 0)
		])
	})
	return output
}
const getAdjusterLegalFeesTotalSum = claims => {
	let output = []
	let groupedTotalAmount = {}
	claims?.reverse().map(claim => {
		// Legal Fee
		let legalCurrencyCode = getClaimFact(
			"legalFeeCurrency",
			"stringValue",
			claim
		)
		let legalFee = getClaimFact("legalFee", "numberValue", claim)
		if (legalCurrencyCode && legalFee) {
			if (!Object.keys(groupedTotalAmount).includes(legalCurrencyCode)) {
				groupedTotalAmount[legalCurrencyCode] = []
			}
			groupedTotalAmount[legalCurrencyCode].push(legalFee)
		}
		// Adjuster Fee
		let adjusterCurrencyCode = getClaimFact(
			"adjusterFeeCurrency",
			"stringValue",
			claim
		)
		let adjusterFee = getClaimFact("adjusterFee", "numberValue", claim)
		if (adjusterCurrencyCode && adjusterFee) {
			if (!Object.keys(groupedTotalAmount).includes(adjusterCurrencyCode)) {
				groupedTotalAmount[adjusterCurrencyCode] = []
			}
			groupedTotalAmount[adjusterCurrencyCode].push(adjusterFee)
		}
	})
	Object.keys(groupedTotalAmount)?.map(objKey => {
		output.push([
			"",
			"Total",
			objKey,
			groupedTotalAmount?.[objKey]?.reduce((sum, item) => {
				return (sum += parseFloat(item))
			}, 0)
		])
	})
	return output
}

const getOutstandingClaimsTotalSum = claims => {
	let output = []
	let groupedTotalReserveAmount = {}
	claims
		?.reverse()
		?.filter(claim => claim?.status === "PROCESSING")
		?.map(claim => {
			let currencyCode = claim?.totalValues?.approvedCurrency
			let reserveAmount = claim?.totalValues?.approvedAmount
			if (!currencyCode) return
			if (!Object.keys(groupedTotalReserveAmount).includes(currencyCode)) {
				groupedTotalReserveAmount[currencyCode] = []
			}

			groupedTotalReserveAmount[currencyCode].push(reserveAmount)
		})
	Object.keys(groupedTotalReserveAmount)?.map(objKey => {
		output.push([
			"",
			"Total",
			objKey,
			groupedTotalReserveAmount?.[objKey]?.reduce((sum, item) => {
				return (sum += parseFloat(item))
			}, 0)
		])
	})
	return output
}

/****************** Function for fetching ******************/
const fetchClaims = async filterPayload => {
	const dateFrom = filterPayload?.createdAt?.after
		? dayjs(filterPayload?.createdAt?.after).format()
		: null
	const dateTo = filterPayload?.createdAt?.before
		? dayjs(filterPayload?.createdAt?.before)
				.add(1, "day")
				.format()
		: null

	const query = gql`
		query fetchClaims($claimWhere: claimWhereInput) {
			claims(where: $claimWhere) {
				list {
					policy {
						associatedContracts {
							contract {
								facts {
									type
									value {
										stringValue
										dateValue
									}
								}
								startDate
							}
						}
						issuerNumber
						facts {
							type
							value {
								stringValue
								dateValue
							}
						}
						contractHolder {
							name
						}
					}
					issuerNumber
					facts {
						type
						value {
							stringValue
							dateValue
							numberValue
						}
					}
					status
					transactions {
						facts {
							type
							value {
								stringValue
								numberValue
								dateValue
							}
						}
						amount
						dateTime
						currencyCode
					}
					totalValues {
						approvedAmount
						approvedCurrency
					}
				}
			}
		}
	`
	const variables = {
		claimWhere: { createdAt_gt: dateFrom, createdAt_lt: dateTo }
	}

	const res = await api({ query, variables })
	//TODO: error handling
	return res?.data?.claims?.list
}

/****************** Export ******************/
export default async filterPayload => {
	const fetchedData = await fetchClaims(filterPayload)
	const workSheetData = [
		...header,
		["1. Claim Settled"],
		...getSettledClaims(fetchedData),
		[],
		...getSettledClaimsTotalSum(fetchedData),
		["2. Adjuster Fee/ Legal Fee"],
		...getAdjusterLegalFees(fetchedData),
		[],
		...getAdjusterLegalFeesTotalSum(fetchedData),
		["3. Claim Outstanding"],
		...getOutstandingClaims(fetchedData),
		[],
		...getOutstandingClaimsTotalSum(fetchedData)
	]
	const workSheetColumns = [
		{ wpx: 200 },
		{ wpx: 200 },
		{ wpx: 200 },
		{ wpx: 200 },
		{ wpx: 200 }
	]
	return { workSheetData, workSheetColumns }
}
